import { ref, onUnmounted } from 'vue'
import { encodeWav } from '../utils/wav-encoder.js'
import { post } from '../api/client.js'
import { useSessionsStore } from '../stores/sessions.js'

export function useAudioRecorder() {
  const isRecording = ref(false)
  const isTranscribing = ref(false)
  const recordingDuration = ref(0)
  const error = ref(null)

  let mediaRecorder = null
  let chunks = []
  let stream = null
  let timer = null

  function startTimer() {
    recordingDuration.value = 0
    timer = setInterval(() => {
      recordingDuration.value++
    }, 1000)
  }

  function stopTimer() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function cleanup() {
    stopTimer()
    if (stream) {
      stream.getTracks().forEach(t => t.stop())
      stream = null
    }
    mediaRecorder = null
    chunks = []
  }

  async function startRecording() {
    error.value = null
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      error.value = window.self !== window.top
        ? 'Microphone blocked — iframe needs allow="microphone"'
        : 'Microphone access denied'
      return
    }

    // Pick best supported MIME type
    const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', '']
      .find(t => !t || MediaRecorder.isTypeSupported(t))
    const options = mimeType ? { mimeType } : undefined

    chunks = []
    mediaRecorder = new MediaRecorder(stream, options)
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data)
    }
    mediaRecorder.start()
    isRecording.value = true
    startTimer()
  }

  function stopMediaRecorder() {
    return new Promise((resolve) => {
      mediaRecorder.onstop = () => resolve()
      mediaRecorder.stop()
    })
  }

  async function stopAndTranscribe() {
    if (!mediaRecorder || mediaRecorder.state !== 'recording') return ''
    error.value = null

    // Stop recording
    await stopMediaRecorder()
    isRecording.value = false
    stopTimer()

    isTranscribing.value = true
    try {
      // Create blob from recorded chunks
      const blob = new Blob(chunks)

      // Decode to AudioBuffer then re-encode as WAV
      const arrayBuffer = await blob.arrayBuffer()
      const audioCtx = new AudioContext()
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer)
      await audioCtx.close()
      const wavBlob = encodeWav(audioBuffer)

      // Convert WAV to base64
      const wavBuffer = await wavBlob.arrayBuffer()
      const base64 = btoa(
        new Uint8Array(wavBuffer).reduce((s, b) => s + String.fromCharCode(b), '')
      )

      // Call transcription API
      // The backend bills transcription to its own provider key, so it is gated on the
      // caller owning the active chat session (sent alongside the credential headers).
      const sessionId = useSessionsStore().activeSessionId
      const { text } = await post('/transcribe', { audio: base64, format: 'wav', sessionId })
      return text || ''
    } catch (e) {
      error.value = e.message || 'Transcription failed'
      return ''
    } finally {
      isTranscribing.value = false
      cleanup()
    }
  }

  function cancelRecording() {
    isRecording.value = false
    cleanup()
  }

  onUnmounted(() => {
    cancelRecording()
  })

  return {
    isRecording,
    isTranscribing,
    recordingDuration,
    error,
    startRecording,
    stopAndTranscribe,
    cancelRecording,
  }
}
